import { alpha, darken, useTheme } from '@material-ui/core';
import Avatar from '@material-ui/core/Avatar';
import LensIcon from '@material-ui/icons/Lens';
import PersonIcon from '@src/resources/icons/PersonIcon';
import { useCallback, useMemo } from 'react';
import IconWrapper from '../IconWrapper';
import useLetterColor, { getMutedColors } from './useLetterColor';

export interface CustomAvatarProps {
  type?: 'default' | 'invert';
  username?: string;
  avatarSrc?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  withStatus?: boolean;
  status?: boolean;
  muted?: boolean;
}

const isLetter = (ch: string) => /[a-zA-Z]/.test(ch);
const avatarSizes = { xs: 24, sm: 28, md: 38, lg: 48 };
const avatarFontSizes = {
  xs: '1rem',
  sm: '1.2rem',
  md: '1.52rem',
  lg: '1.85rem',
};

const CustomAvatar: React.FC<CustomAvatarProps> = ({
  username,
  avatarSrc,
  withStatus,
  size = 'md',
  status,
  type = 'default',
  muted,
}) => {
  const theme = useTheme();

  const getFirstLetterName = useCallback(
    (name: string, locale = 'es-CO'): string | undefined => {
      const firstChar = name?.trim()?.[0] ?? '';

      return isLetter(firstChar)
        ? firstChar.toLocaleUpperCase(locale)
        : undefined;
    },
    []
  );

  const bgColor = useLetterColor(getFirstLetterName(username || ''));
  const mutedColors = useMemo(() => getMutedColors(bgColor), [bgColor]);

  const isDark = theme.palette.type === 'dark';
  const isMuted = muted ?? isDark;

  const getColorStyle = (): Pick<
    React.CSSProperties,
    'backgroundColor' | 'color'
  > => {
    if (isMuted)
      return { backgroundColor: mutedColors.bg, color: mutedColors.text };
    if (type === 'default') {
      return {
        backgroundColor: bgColor,
        color: theme.palette.primary.contrastText,
      };
    }
    return {
      backgroundColor: alpha(bgColor, 0.4),
      color: darken(bgColor, 0.15),
    };
  };

  const avatarStyles: React.CSSProperties = {
    fontWeight: 600,
    width: avatarSizes[size],
    height: avatarSizes[size],
    fontSize: avatarFontSizes[size],
    ...getColorStyle(),
  };

  return (
    <div style={{ position: 'relative' }}>
      <Avatar style={avatarStyles} src={avatarSrc}>
        {(username && getFirstLetterName(username)) || (
          <IconWrapper fontSize="20px">
            <PersonIcon style={{ marginTop: -2 }} />
          </IconWrapper>
        )}
      </Avatar>
      {/* Legacy status */}
      {withStatus && (
        <span
          style={{
            color: status ? '#8bc34a' : '#f44336',
            position: 'absolute',
            bottom: '-15%',
            right: 0,
          }}
        >
          <LensIcon color="inherit" fontSize="small" />
        </span>
      )}
    </div>
  );
};

export default CustomAvatar;
